import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Avatar, Box, Button, Tag, Text, useToggle } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useBudgetMovementsDatatable } from 'generated/resources/datatable.graphql'
import { BudgetMovementFragment, ExpenseCategoryFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import tagColor from 'modules/expenses/lib/tagColors'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

import MovementDetail from './Detail'
import EmptySpendings from './Empty'
import CategoryFilter from './Filters/category'
import EmployeeFilter from './Filters/employee'
import StatusFilter from './Filters/status'

interface Props {
  movements: BudgetMovementFragment[]
  categories: ExpenseCategoryFragment[]
  periodStart?: string
  periodEnd?: string
}

const SpendingsTab = ({ movements, categories, periodStart, periodEnd }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { budgetId } = useParams()

  const [showDetail, toggleDetail] = useToggle(false)
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()

  const [selectedId, setSelectedId] = useState<number>()

  const source = useBudgetMovementsDatatable(
    [
      {
        identifier: 'employee',
        title: i18n.t('budgets.detail.tabs.spendings.table.employee'),
        kind: ProjectionKind.Custom,
        template: '',
        colSpan: 3,
        render: ({ employee }) => {
          if (!employee) return
          return (
            <Box flexDirection="row" alignItems="center" gap="s16">
              <Avatar label={employee.fullName} url={buildUrl(employee.avatar?.url)} size="s32" />
              <Text.Normal weight="medium">{employee.fullName}</Text.Normal>
            </Box>
          )
        },
      },
      {
        identifier: 'category',
        title: i18n.t('budgets.detail.tabs.spendings.table.category'),
        kind: ProjectionKind.Custom,
        template: '',
        colSpan: 2,
        render: ({ expensable }) => {
          if (!expensable) return

          const categoryName =
            expensable.expense?.category?.name ??
            expensable.mileage?.category?.name ??
            expensable.perDiem?.category?.name

          return (
            <Box width="fit-content">
              <Tag leftItem="circle" showCircle type="grey">
                {categoryName}
              </Tag>
            </Box>
          )
        },
      },
      {
        identifier: 'status',
        title: i18n.t('budgets.detail.tabs.spendings.table.status'),
        kind: ProjectionKind.Custom,
        template: '',
        colSpan: 2,
        render: ({ expensable }) => {
          if (!expensable) return
          return (
            <Box width="fit-content">
              <Tag leftItem="circle" showCircle type={tagColor(expensable.status)}>
                {getStatusLabel(expensable.status)}
              </Tag>
            </Box>
          )
        },
      },
      {
        identifier: 'effectiveAt',
        title: i18n.t('budgets.detail.tabs.spendings.table.document_date'),
        kind: ProjectionKind.Custom,
        sortBy: 'effectiveAt',
        template: '',
        colSpan: 2,
        render: ({ effectiveAt }) => {
          return <Text.Normal>{dateBackendDateTimeToUI(effectiveAt)}</Text.Normal>
        },
      },
      {
        identifier: 'amountCents',
        title: i18n.t('budgets.detail.tabs.spendings.table.amount'),
        kind: ProjectionKind.Custom,
        sortBy: 'amountCents',
        template: '',
        colSpan: 2,
        render: ({ amountCents, targetCurrency }) => {
          if (!amountCents) return
          return (
            <Text.Normal weight="medium">
              {amountWithCurrency(amountCents, targetCurrency, locale)}
            </Text.Normal>
          )
        },
      },
      {
        identifier: 'expensableDetail',
        kind: ProjectionKind.Decorative,
        colSpan: 1,
        render: ({ id }) => {
          return (
            <Box width="fit-content" justifyContent="center" alignItems="center">
              <Button
                type="secondary"
                iconName="arrow-right"
                onClick={() => {
                  setSelectedId(id)
                  toggleDetail(true)()
                }}
              />
            </Box>
          )
        },
      },
    ],
    {
      variables: {
        id: Number(budgetId),
        search: debouncedSearch,
        periodStart: periodStart,
        periodEnd: periodEnd,
      },
    }
  )

  const filters = [
    EmployeeFilter({ source: source, movements: movements }),
    CategoryFilter({ source: source, categories: categories }),
    StatusFilter({ source: source }),
  ]
  return (
    <DataTable.Provider
      source={source}
      sort={{ path: 'effectiveAt', direction: 'desc' }}
      filters={filters}
      pagination={{
        kind: 'cursor',
        itemsPerPage: 50,
      }}
    >
      <Box gap="s16">
        <DataTable.HeadBar
          search={{
            placeholder: i18n.t('common.search'),
            value: search,
            onChange: onSearch,
          }}
          disableExport
          disableEditColumns
        />
        <DataTable.Table emptyState={<EmptySpendings />} />
      </Box>
      {showDetail && selectedId && (
        <MovementDetail movementId={selectedId} onClose={toggleDetail(false)} />
      )}
    </DataTable.Provider>
  )
}

export default SpendingsTab
